import { useCurrencyContext } from '@apeiron/currency'
import { OrbitalTrackRentalPopupType } from '@apeiron/orbital'
import { PlanetLite } from '@apeiron/planet'
import {
  ApeironBlueButton,
  ApeironInfinityDataGrid,
  ApeironInfinityDataGridRef,
  InfinityDatasourceResult,
  MayNull,
  SearchEmptyState,
  useDate
} from '@apeiron/temp'
import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import MultipleRentalDialog from '@src/components/orbitalTrack/detachRenew/MultipleRentalDialog'
import ApeironTwoColorMessage from '@src/components/share/apeiron/ApeironTwoColorMessage'
import DateLinearProgress from '@src/components/share/generic/DateLinearProgress'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import { OrbitalTrackRenewDetachProvider } from '@src/contexts/orbitalTrack/OrbtialTrackRenewDetachContext'
import { useNowContext } from '@src/contexts/share/NowContext'
import useOrbitalTrackHelper from '@src/hooks/apeiron/orbitalTrack/useOrbitalTrackHelper'
import theme from '@src/styles/theme'
import { OrbitalTrackLite } from '@src/types/orbitalTrack'
import {
  ColDef,
  ICellRendererParams,
  IRowNode,
  SelectionChangedEvent
} from 'ag-grid-community'
import { differenceInSeconds, isBefore, subHours } from 'date-fns'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const StyledApeironInfinityDataGrid = styled(ApeironInfinityDataGrid)`
  background-color: #0e0e12;
  border-radius: 0px;
  width: 100%;
  max-height: 500px;
  overflow: hidden;
  .Custom-Grid {
    overflow: unset;
    .ag-header {
      color: white;
      font-weight: 400;
      margin-bottom: -1px;
    }
    .ag-header-row {
      background-color: #151615;
    }
    .ag-cell {
      color: white;
      background-color: #0e0e12;
      border-bottom: 1px solid #232326;
    }
  }
`

const PlanetCellText = styled(ApeironTwoColorMessage)`
  color: white;
  font-size: 16px;
  width: 100%;
  white-space: pre-line;
  overflow-x: hidden;
  text-overflow: ellipsis;
  .Custom-First-Color-Text {
    color: #3377ff;
    font-size: 16px;
    font-weight: 300;
    white-space: pre-line;
  }
  .Custom-Second-Color-Text {
    color: #80807e;
    font-size: 16px;
    font-weight: 300;
    white-space: pre-line;
  }
`

const CooldownCell = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StyledDateLinearProgress = styled(DateLinearProgress)`
  width: 100%;
`

const TimeCellText = styled(Typography)`
  color: #80807e;
  font-size: 16px;
  strong {
    color: white;
    font-weight: 400;
  }
`

const TagCellText = styled(Typography)`
  color: black;
  font-size: 14px;
  font-weight: 400;
  background-color: #febe36;
  border-radius: 4px;
  padding: 2px 8px;
`

const ButtonContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

const EndButton = styled(ApeironBlueButton)`
  border-radius: 4px;
  font-weight: 400;
  width: 140px;
`

const PlanetCell: FC<CellProps> = props => {
  const { track } = props

  const planet = R.propOr(null, 'planet', track) as PlanetLite | null

  const { t } = useTranslation()

  return (
    <PlanetCellText
      message={t('godiverse.orbital_track.planet_filled_detail', {
        name: planet?.name || t('common.not_applicable'),
        address: planet?.owner.name || t('common.not_applicable'),
        count: R.isNil(planet) ? 0 : 1
      })}
    />
  )
}

const PlanetAgeCell: FC<CellProps> = props => {
  const { track } = props

  const { t } = useTranslation()

  const planet = R.propOr(null, 'planet', track) as MayNull<PlanetLite>

  return t('common.i18n_format.year_old_other', { count: planet?.age || 0 })
}

const RentCell: FC<CellProps> = props => {
  const { track } = props

  const { getApeironPriceWithUnit } = useCurrencyContext()

  return (
    <>
      {getApeironPriceWithUnit(
        track?.setting.currencyType,
        track?.setting.rentalFee
      )}
    </>
  )
}

const RentPerDayCell: FC<CellProps> = props => {
  const { track } = props

  const { t } = useTranslation()

  const { getApeironPriceWithUnit } = useCurrencyContext()

  if (R.isNil(track)) {
    return <>{' - '}</>
  }

  const duration: number = track?.setting.rentalDuration || 0

  const feeBN = BigInt(track?.rentalFee || '0')

  const dailyFeeBN = feeBN / BigInt(duration)

  return t('orbit.list.grid.daily_fee', {
    fee: getApeironPriceWithUnit(
      track?.setting.currencyType,
      dailyFeeBN.toString()
    )
  })
}

const TimeCell: FC<CellProps> = props => {
  const { track } = props

  const { now: getNow } = useNowContext()

  const { trackCooldown } = useOrbitalTrackAttachContext()

  const { checkInCooldown } = useOrbitalTrackHelper()

  const { t } = useTranslation()

  const now = useMemo((): Date => getNow(), [getNow])

  if (R.isNil(track)) {
    return <></>
  }

  const inCooldown = checkInCooldown(track)

  if (inCooldown) {
    const cooldownEnd = new Date(track.availableTrackTime)

    const cooldownStart = subHours(cooldownEnd, trackCooldown)

    return (
      <CooldownCell>
        <TimeCellText>
          <Trans>
            {t('godiverse.orbital_track.cd_remain', {
              count: differenceInSeconds(now, cooldownEnd)
            })}
          </Trans>
        </TimeCellText>
        <StyledDateLinearProgress
          startAt={cooldownStart}
          endAt={cooldownEnd}
          now={now}
        />
      </CooldownCell>
    )
  }

  const rentEnd = new Date(track.rentalEndTime)

  const renting = isBefore(now, rentEnd)

  if (!renting) {
    return (
      <TagCellText>
        {t(
          track?.renewable
            ? 'godiverse.orbital_track.renewable'
            : 'orbit.list.grid.ended'
        )}
      </TagCellText>
    )
  }

  return (
    <TimeCellText>
      <Trans>
        {t('godiverse.orbital_track.rented_remain', {
          count: differenceInSeconds(now, rentEnd)
        })}
      </Trans>
    </TimeCellText>
  )
}

const TrackDataGrid: FC<Props> = props => {
  const { className, inviteGroupId } = props

  const ref = useRef<ApeironInfinityDataGridRef | null>(null)

  const mobile = useMediaQuery(theme.breakpoints.down('res768'))

  const [empty, setEmpty] = useState<boolean>(true)

  const [endTracks, setEndTracks] = useState<OrbitalTrackLite[]>([])

  const [open, setOpen] = useState<boolean>(false)

  const { dateValid } = useDate()

  const { t } = useTranslation()

  const { trackDetail, refetchStar } = useOrbitalTrackAttachContext()

  const { checkInCooldown } = useOrbitalTrackHelper()

  const groupTracks = useMemo(() => {
    return !R.isNil(trackDetail)
      ? R.filter<OrbitalTrackLite, OrbitalTrackLite[]>(
          (track: OrbitalTrackLite) => {
            return (
              R.pathOr<string>('', ['setting', 'inviteGroupId'], track) ===
              inviteGroupId
            )
          },
          trackDetail.tracks
        )
      : []
  }, [trackDetail, inviteGroupId])

  const columns = useMemo((): ColDef<any, OrbitalTrackLite>[] => {
    return [
      {
        field: 'start',
        headerName: '',
        pinned: 'left',
        width: mobile ? 0 : 20
      },
      {
        cellClass: 'custom-ag-cell-center',
        checkboxSelection: true,
        headerCheckboxSelection: true, // ! header checkbox not working on infinite scroll
        headerClass: 'custom-ag-header-center',
        pinned: 'left',
        showDisabledCheckboxes: mobile,
        width: 80
      },
      {
        field: 'planet',
        flex: 1,
        headerName: t('orbit.list.grid.planet_detail'),
        minWidth: 220,
        cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
          const { data = null } = params

          return <PlanetCell track={data} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'aging_buff',
        headerClass: 'custom-ag-header-center',
        minWidth: 130,
        headerName: t('orbit.list.grid.age'),
        cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
          const { data = null } = params

          return <PlanetAgeCell track={data} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'rent',
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.price'),
        minWidth: 120,
        cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
          const { data = null } = params

          return <RentCell track={data} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'rent_per_day',
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.price_per_day'),
        minWidth: 160,
        cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
          const { data = null } = params

          return <RentPerDayCell track={data} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'time',
        flex: 1,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.time_remain'),
        minWidth: 280,
        cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
          const { data = null } = params

          return <TimeCell track={data} />
        }
      },
      {
        field: 'end',
        headerName: '',
        width: mobile ? 0 : 20
      }
    ]
  }, [mobile, t])

  const handleOnFetch = useCallback(
    async (
      page: number,
      pageSize: number
    ): Promise<InfinityDatasourceResult> => {
      const startIndex = (page - 1) * pageSize

      const endIndex = startIndex + pageSize

      const data = R.slice(startIndex, endIndex, groupTracks)

      const total = R.length(groupTracks)

      setEmpty(total === 0)

      return { data, total }
    },
    [groupTracks]
  )

  const handleOnNoRowsOverlay = useCallback(
    () => (
      <SearchEmptyState description={t('godiverse.orbital_track.no_planets')} />
    ),
    [t]
  )

  const handleOnSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedTracks = event.api.getSelectedRows()

      setEndTracks(
        R.filter(track => !dateValid(track.avaiableTrackTime), selectedTracks)
      )
    },
    [dateValid]
  )

  const handleRowSelectable = useCallback(
    (params: IRowNode<OrbitalTrackLite>): boolean => {
      const track = R.prop('data', params)

      const inCooldown = checkInCooldown(track)

      return !inCooldown
    },
    [checkInCooldown]
  )

  const handleOnEndClick = useCallback(() => {
    setOpen(true)
  }, [])

  const handleOnClose = useCallback(
    (successIds: string[]) => {
      setOpen(false)
      if (!R.isEmpty(successIds)) {
        setEndTracks([])
        refetchStar()
      }
    },
    [refetchStar]
  )

  useEffect(() => {
    if (ref.current) {
      ref.current.refreshData()
    }
  }, [groupTracks])

  useEffect(() => {
    if (ref.current) {
      ref.current.deselectAll()
    }

    setEndTracks([])
  }, [trackDetail?.star?.id])

  return (
    <Container className={className}>
      <StyledApeironInfinityDataGrid
        columnDefs={columns}
        gridRef={ref}
        rowSelection={{
          enableClickSelection: true,
          mode: 'multiRow',
          isRowSelectable: handleRowSelectable
        }}
        fetch={handleOnFetch}
        noRowsOverlayComponent={handleOnNoRowsOverlay}
        onSelectionChanged={handleOnSelectionChanged}
      />
      {!empty && (
        <ButtonContainer>
          <EndButton disabled={R.isEmpty(endTracks)} onClick={handleOnEndClick}>
            {t('common.button.end')}
          </EndButton>
          <OrbitalTrackRenewDetachProvider>
            <MultipleRentalDialog
              isOpened={open}
              popupType={OrbitalTrackRentalPopupType.Star_Owner}
              tracks={endTracks}
              onClose={handleOnClose}
            />
          </OrbitalTrackRenewDetachProvider>
        </ButtonContainer>
      )}
    </Container>
  )
}

type CellProps = {
  track: OrbitalTrackLite | null
}

type Props = {
  className?: string
  inviteGroupId: string
}

export default TrackDataGrid
